import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Compass, 
  TrendingUp, 
  Handshake, 
  Target,
  Anchor,
  Wind,
  ShipWheel
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1760291007478-737c71783c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjB5YWNodCUyMHNhaWxpbmclMjBvY2VhbiUyMGRhcmslMjBibHVlfGVufDB8fHx8MTc3MzE2NTYzMnww&ixlib=rb-4.1.0&q=85";
const COMPASS_IMG = "https://images.unsplash.com/photo-1488375634201-b85b28653a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxhbnRpcXVlJTIwbmF1dGljYWwlMjBjb21wYXNzJTIwY2hhcnQlMjBjbG9zZSUyMHVwJTIwZGFya3xlbnwwfHx8fDE3NzMxNjU2NDd8MA&ixlib=rb-4.1.0&q=85";
const BRIDGE_IMG = "https://images.unsplash.com/photo-1560964645-5995275a218d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwYnJpZGdlJTIwc3VzcGVuc2lvbiUyMGZvZyUyMGFic3RyYWN0fGVufDB8fHx8MTc3MzE2NTY0OXww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } }
};

const services = [
  {
    icon: Compass,
    title: "Strategic Advisory",
    description: "Executive-level consulting focused on growth strategy and leadership development.",
    size: "large"
  },
  {
    icon: ShipWheel,
    title: "Operational Oversight",
    description: "Hands-on management support for internal structure and accountability.",
    size: "small"
  },
  {
    icon: TrendingUp,
    title: "Business Transformation",
    description: "Restructuring and repositioning for sustainable success.",
    size: "small"
  },
  {
    icon: Handshake,
    title: "Investment & Partnership",
    description: "Selective investment into aligned businesses.",
    size: "large"
  }
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-end pb-24 md:pb-32"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Ocean sailing"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 px-6 md:px-12 lg:px-24 max-w-4xl"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-6"
            variants={fadeUp}
          >
            Strategic Business Consulting
          </motion.p>
          <motion.h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-none mb-8"
            variants={fadeUp}
          >
            Strategic Leadership for Businesses Ready to Grow.
          </motion.h1>
          <motion.p 
            className="text-slate-300 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl"
            variants={fadeUp}
          >
            Venture Winds partners with select businesses that require centralized 
            leadership, operational oversight, and strategic direction to reach 
            their full potential.
          </motion.p>
          <motion.div className="flex flex-col sm:flex-row gap-4" variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-navy-950 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium btn-shadow hover:bg-teal-600 transition-colors"
              data-testid="hero-cta-primary"
            >
              Begin Your Journey
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-white hover:text-navy-950 transition-all"
              data-testid="hero-cta-secondary"
            >
              Learn More
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div 
              className="w-1 h-2 bg-teal-500 rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            />
          </div>
        </motion.div>
      </section>

      {/* What We Do - Bento Grid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="services-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            What We Do
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-5xl font-semibold text-navy-950 mb-6 max-w-2xl"
            variants={fadeUp}
          >
            Anchoring Businesses in Excellence
          </motion.h2>
          <motion.p 
            className="text-slate-500 text-lg leading-relaxed max-w-2xl mb-16"
            variants={fadeUp}
          >
            We provide executive-level consulting, operational restructuring, and 
            investment capital to help companies stabilize, grow, and thrive.
          </motion.p>

          {/* Bento Grid */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={fadeUp}
                className={`service-card bg-white border border-slate-100 p-8 md:p-10 hover:border-teal-500/50 transition-all duration-300 group ${
                  service.size === "large" ? "lg:col-span-2" : ""
                }`}
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-8 h-8 text-teal-500 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl md:text-2xl font-medium text-navy-950 mb-4">
                  {service.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="mt-12 text-center" variants={fadeUp}>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-navy-950 text-sm uppercase tracking-widest font-medium hover:text-teal-600 transition-colors link-underline"
              data-testid="services-link"
            >
              Explore All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Our Approach Preview */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950" data-testid="approach-preview">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p 
              className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
              variants={fadeUp}
            >
              Our Approach
            </motion.p>
            <motion.h2 
              className="font-playfair text-3xl md:text-5xl font-semibold text-white mb-6"
              variants={fadeUp}
            >
              Navigating Your Path to Success
            </motion.h2>
            <motion.p 
              className="text-slate-400 text-lg leading-relaxed mb-8"
              variants={fadeUp}
            >
              Unlike traditional consulting firms, we operate as hands-on strategic 
              partners. We identify, evaluate, implement, and guide—working closely 
              with leadership every step of the journey.
            </motion.p>
            <motion.ul className="space-y-4 mb-10" variants={staggerContainer}>
              {[
                "Identify promising businesses",
                "Evaluate operational and leadership needs",
                "Implement structured management oversight",
                "Guide long-term growth and sustainability"
              ].map((item, i) => (
                <motion.li 
                  key={i}
                  className="flex items-center gap-4 text-slate-300"
                  variants={fadeUp}
                >
                  <div className="w-8 h-8 rounded-full border border-teal-500 flex items-center justify-center text-teal-500 text-sm font-semibold">
                    {i + 1}
                  </div>
                  {item}
                </motion.li>
              ))}
            </motion.ul>
            <motion.div variants={fadeUp}>
              <Link
                to="/approach"
                className="inline-flex items-center gap-2 bg-teal-500 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-teal-600 transition-colors"
                data-testid="approach-link"
              >
                Our Methodology
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <img
              src={COMPASS_IMG}
              alt="Nautical compass on chart"
              className="w-full aspect-square object-cover"
            />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-teal-500 flex items-center justify-center">
              <Anchor className="w-12 h-12 text-white" strokeWidth={1.5} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Strategic Partnerships */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="partnerships-preview">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <img
              src={BRIDGE_IMG}
              alt="Bridge in fog"
              className="w-full aspect-[4/3] object-cover"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="order-1 lg:order-2"
          >
            <motion.p 
              className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
              variants={fadeUp}
            >
              Strategic Partnerships
            </motion.p>
            <motion.h2 
              className="font-playfair text-3xl md:text-5xl font-semibold text-navy-950 mb-6"
              variants={fadeUp}
            >
              Selective by Design
            </motion.h2>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed mb-8"
              variants={fadeUp}
            >
              Venture Winds does not work with every company. We partner with 
              businesses that align with our strategic vision, demonstrate growth 
              potential, and value structured leadership.
            </motion.p>
            <motion.div 
              className="space-y-6 mb-10"
              variants={staggerContainer}
            >
              {[
                { icon: Target, text: "Align with our strategic vision" },
                { icon: TrendingUp, text: "Demonstrate potential for growth" },
                { icon: Handshake, text: "Value structured leadership and accountability" }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  className="flex items-center gap-4"
                  variants={fadeUp}
                >
                  <div className="w-12 h-12 bg-slate-100 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                  </div>
                  <span className="text-navy-950 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
            <motion.div variants={fadeUp}>
              <Link
                to="/partnerships"
                className="inline-flex items-center gap-2 text-navy-950 text-sm uppercase tracking-widest font-medium hover:text-teal-600 transition-colors link-underline"
                data-testid="partnerships-link"
              >
                Partnership Criteria
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Investment Philosophy */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="investment-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.p 
            className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Investment Philosophy
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-5xl font-semibold text-navy-950 mb-6"
            variants={fadeUp}
          >
            Capital with Purpose
          </motion.h2>
          <motion.p 
            className="text-slate-500 text-lg leading-relaxed mb-12"
            variants={fadeUp}
          >
            Beyond consulting, Venture Winds may invest capital and resources into 
            companies to help them launch, restructure, or scale successfully. Our 
            investments are strategic, selective, and focused on long-term value creation.
          </motion.p>
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
          >
            {[
              { value: "Strategic", label: "Investment Approach" },
              { value: "Long-term", label: "Value Creation" },
              { value: "Hands-on", label: "Partnership Style" }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                className="text-center"
                variants={fadeUp}
              >
                <p className="font-playfair text-4xl md:text-5xl font-bold text-teal-600 mb-2">
                  {stat.value}
                </p>
                <p className="text-slate-500 text-sm uppercase tracking-widest">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950" data-testid="cta-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-3xl mx-auto text-center"
        >
          <motion.div variants={fadeUp} className="mb-8">
            <Wind className="w-16 h-16 text-teal-500 mx-auto" strokeWidth={1} />
          </motion.div>
          <motion.h2 
            className="font-playfair text-3xl md:text-5xl font-semibold text-white mb-6"
            variants={fadeUp}
          >
            Ready to Chart Your Course?
          </motion.h2>
          <motion.p 
            className="text-slate-400 text-lg leading-relaxed mb-10"
            variants={fadeUp}
          >
            Connect with Venture Winds to explore how we can help your business 
            achieve operational excellence and sustainable growth.
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-10 py-5 text-sm uppercase tracking-widest font-medium hover:bg-teal-500 hover:text-white transition-all"
              data-testid="cta-contact"
            >
              Start a Conversation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
